/**
 * FAQAT ISHLAB CHIQISH UCHUN — soxta ma'lumotlar.
 *
 * `npm run dev:mock` bilan yoqiladi (VITE_MOCK=1). Backend va Postgres
 * bo'lmasa ham interfeysni ko'rish va sozlash imkonini beradi.
 *
 * Ishlab chiqarish build'iga TUSHMAYDI: VITE_MOCK doimiy "0" bo'lgani
 * uchun Vite bu shoxni tashlab ketadi (tree-shaking).
 */

import type {
  FeedResponse,
  HomeResponse,
  MeResponse,
  OnboardingResponse,
  OpportunityCard,
  OpportunityDetail,
  SaveResponse,
  StartParamResponse,
  TaxonomyResponse,
} from "./types";

const today = new Date();
function inDays(days: number): string {
  const d = new Date(today);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const CARDS: OpportunityCard[] = [
  {
    id: 482, title: "Chevening: Britaniyada magistratura granti",
    organization: "UK Government", host_country: "GB", type: "talim",
    degree_level: ["master"], funding: "full", format: "onsite",
    deadline_at: inDays(5), deadline_type: "fixed", days_left: 5,
    flags: { no_ielts: false, no_fee: true }, saved: false, score: 87,
  },
  {
    id: 371, title: "DAAD EPOS: rivojlanish uchun magistratura stipendiyasi",
    organization: "DAAD", host_country: "DE", type: "talim",
    degree_level: ["master", "phd"], funding: "full", format: "onsite",
    deadline_at: inDays(26), deadline_type: "fixed", days_left: 26,
    flags: { no_ielts: true, no_fee: true }, saved: true, score: 81,
  },
  {
    id: 205, title: "Türkiye Bursları: to'liq moliyalashtiriladigan grant",
    organization: "Türkiye Bursları", host_country: "TR", type: "talim",
    degree_level: ["bachelor", "master"], funding: "full", format: "onsite",
    deadline_at: inDays(12), deadline_type: "fixed", days_left: 12,
    flags: { no_ielts: true, no_fee: true }, saved: false, score: 74,
  },
  {
    id: 118, title: "Work & Travel USA: yozgi ish dasturi",
    organization: "American Councils", host_country: "US", type: "almashinuv",
    degree_level: ["bachelor"], funding: "unpaid", format: "onsite",
    deadline_at: inDays(45), deadline_type: "fixed", days_left: 45,
    flags: { no_experience: true }, saved: false, score: 62,
  },
  {
    id: 903, title: "Google Summer of Code: ochiq kodli loyihalarda amaliyot",
    organization: "Google", host_country: null, type: "ish",
    degree_level: ["any"], funding: "stipend_only", format: "online",
    deadline_at: null, deadline_type: "rolling", days_left: null,
    flags: { no_ielts: true, no_experience: true, no_fee: true },
    saved: false, score: 58,
  },
  {
    id: 640, title: "MUN Astana 2027: xalqaro talabalar konferensiyasi",
    organization: "Nazarbayev University", host_country: "KZ", type: "tadbir",
    degree_level: ["bachelor", "master"], funding: "travel_only",
    format: "onsite", deadline_at: inDays(3), deadline_type: "fixed",
    days_left: 3, flags: { no_fee: true }, saved: false, score: 51,
  },
];

const DETAILS: Record<number, Partial<OpportunityDetail>> = {
  482: {
    description_uz:
      "Britaniya hukumatining xalqaro stipendiya dasturi. Bir yillik " +
      "magistratura o'qishi to'liq qoplanadi. Nomzodlardan kamida ikki " +
      "yillik ish tajribasi va yetakchilik salohiyati talab qilinadi.",
    benefits: ["To'liq o'qish to'lovi", "Oylik stipendiya", "Aviabilet",
               "Viza to'lovi"],
    documents: ["Motivatsion xat", "Ikki tavsiyanoma", "Diplom tarjimasi",
                "IELTS sertifikati", "Pasport nusxasi"],
    language_req: "IELTS 6.5 (umumiy)", text_lang: "uz", text_is_fallback: false, restriction: null,
    excluded_countries: [], eligibility_evidence: null,
    apply_url: "https://apply.chevening.org/",
  },
  371: {
    description_uz:
      "Rivojlanayotgan davlatlar mutaxassislari uchun Germaniyadagi " +
      "magistratura va PhD dasturlari. Ariza bevosita universitetga " +
      "beriladi, DAAD esa moliyalashtiradi.",
    benefits: ["Oylik 934 evro", "Sug'urta", "Yo'l xarajati"],
    language_req: "Ba'zi dasturlarda universitet testi yetarli",
    text_lang: "uz", text_is_fallback: false, restriction: null, excluded_countries: [],
    eligibility_evidence: null, apply_url: "https://www.daad.de/en/",
  },
};

function detailOf(id: number): OpportunityDetail {
  const card = CARDS.find((c) => c.id === id) ?? CARDS[0];
  return {
    ...card,
    title_original: null,
    description_uz: null,
    benefits: [],
    documents: [],
    language_req: null,
    age_min: null,
    age_max: null,
    requires_enrollment: null,
    text_lang: "uz", text_is_fallback: false,
    restriction: "Quyidagi davlatlar fuqarolari uchun ochiq emas: RU",
    excluded_countries: ["RU"],
    eligibility_evidence: "not open to Russian citizens",
    eligible_countries: ["UZ", "KZ", "KG"],
    apply_url: null,
    source_url: "https://example.org/grant",
    source_name: "Namuna manba",
    status: "published",
    is_closed: false,
    reminder_enabled: card.saved,
    published_at: null,
    ...DETAILS[id],
  };
}

let me: MeResponse = {
  lang: "uz", suggested_lang: null,
  tg_id: 1, first_name: "Ali", onboarded: true, status: "bakalavr_tugagan",
  birth_year: 2002, fields: ["it"], ielts_status: "no", gender: null,
  saved_count: 1, reminder_count: 1, notif_digest: true,
  notif_new_match: false, notif_saved_changed: true,
};

const saved = new Set<number>([371]);

function feed(items: OpportunityCard[]): FeedResponse {
  return { items, total: items.length, offset: 0, limit: 30 };
}

const wait = <T,>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), 180));

export const mockApi = {
  start: () => wait<StartParamResponse>({ opportunity_id: null, source: null, link_lang: null }),
  me: () => wait(me),
  taxonomy: () =>
    wait<TaxonomyResponse>({
      types: [
        { key: "talim", label: "Ta'lim", emoji: "🎓" },
        { key: "almashinuv", label: "Almashinuv", emoji: "🔄" },
        { key: "tadqiqot", label: "Tadqiqot", emoji: "🔬" },
        { key: "ish", label: "Ish va amaliyot", emoji: "💼" },
        { key: "tadbir", label: "Tadbirlar", emoji: "🎤" },
        { key: "trening", label: "Trening", emoji: "📚" },
        { key: "tanlov", label: "Tanlovlar", emoji: "🏆" },
        { key: "loyiha", label: "Loyiha granti", emoji: "💰" },
      ],
      degree_levels: ["secondary", "bachelor", "master", "phd", "postdoc", "any"],
      funding: ["full", "partial", "travel_only", "stipend_only", "free"],
      formats: ["onsite", "online", "hybrid"],
      fields: ["it", "stem", "medicine", "business", "law", "arts", "social",
               "education", "agriculture"],
      continents: [
        { key: "europe", label: "Yevropa" },
        { key: "asia", label: "Osiyo" },
        { key: "africa", label: "Afrika" },
        { key: "north_america", label: "Shimoliy Amerika" },
        { key: "south_america", label: "Janubiy Amerika" },
        { key: "oceania", label: "Okeaniya" },
      ],
      user_statuses: ["maktab", "bakalavr_talaba", "bakalavr_tugagan",
                      "magistr_talaba", "magistr_tugagan", "ishlayapti"],
      ielts_statuses: ["yes", "no", "preparing"],
      flags: ["no_ielts", "no_experience", "no_fee", "women_only",
              "is_paid_position"],
    }),

  home: () =>
    wait<HomeResponse>({
      onboarded: me.onboarded,
      quick_filters: [
        { key: "no_ielts", label: "IELTSsiz", count: 3 },
        { key: "full_funding", label: "To'liq grant", count: 3 },
        { key: "deadline_soon", label: "2 hafta ichida", count: 3 },
      ],
      for_you: CARDS.map((c) => ({ ...c, saved: saved.has(c.id) })),
      categories: [
        { type: "talim", label: "Ta'lim", emoji: "🎓", count: 124 },
        { type: "ish", label: "Ish va amaliyot", emoji: "💼", count: 89 },
        { type: "tadbir", label: "Tadbirlar", emoji: "🎤", count: 45 },
        { type: "tanlov", label: "Tanlovlar", emoji: "🏆", count: 31 },
        { type: "almashinuv", label: "Almashinuv", emoji: "🔄", count: 22 },
        { type: "trening", label: "Trening", emoji: "📚", count: 14 },
        { type: "tadqiqot", label: "Tadqiqot", emoji: "🔬", count: 9 },
        { type: "loyiha", label: "Loyiha granti", emoji: "💰", count: 0 },
      ],
      deadline_soon: CARDS.filter((c) => c.days_left !== null && c.days_left <= 14)
        .map((c) => ({ ...c, saved: saved.has(c.id) })),
      saved_count: saved.size,
    }),

  onboarding: (payload: Record<string, unknown>) => {
    me = { ...me, ...payload, onboarded: true } as MeResponse;
    return wait<OnboardingResponse>({ onboarded: true, match_count: 47 });
  },

  // Sahifalashni ("Ko'rishni davom ettirish") ko'rish uchun 6 ta
  // namuna 8 marta takrorlanadi (har nusxa o'z `id` si bilan) va
  // `offset`/`limit` hurmat qilinadi — xuddi haqiqiy API kabi.
  search: (query: { q?: string; type?: string; offset?: number; limit?: number }) => {
    let items = Array.from({ length: 8 }, (_, k) =>
      CARDS.map((c) => ({ ...c, id: c.id + k * 10000 })),
    ).flat().map((c) => ({ ...c, saved: saved.has(c.id) }));
    if (query.type) items = items.filter((c) => c.type === query.type);
    if (query.q) {
      const needle = query.q.toLowerCase();
      items = items.filter((c) => c.title.toLowerCase().includes(needle));
    }
    const offset = query.offset ?? 0;
    const limit = query.limit ?? 30;
    return wait<FeedResponse>({
      items: items.slice(offset, offset + limit), total: items.length, offset, limit,
    });
  },

  detail: (id: number) =>
    wait({ ...detailOf(id), saved: saved.has(id), reminder_enabled: saved.has(id) }),

  similar: (id: number) =>
    wait(CARDS.filter((c) => c.id !== id).slice(0, 3)
      .map((c) => ({ ...c, saved: saved.has(c.id) }))),

  save: (id: number) => {
    saved.add(id);
    return wait<SaveResponse>({ saved: true, reminder_enabled: true,
                                saved_count: saved.size, limit_reached: false });
  },

  unsave: (id: number) => {
    saved.delete(id);
    return wait<SaveResponse>({ saved: false, reminder_enabled: false,
                                saved_count: saved.size, limit_reached: false });
  },

  saved: () =>
    wait(feed(CARDS.filter((c) => saved.has(c.id))
      .map((c) => ({ ...c, saved: true })))),

  // Usiz mock rejimida yurak tugmasi va kartaga sichqoncha olib
  // borish xato tashlardi — realApi'da ular bor
  like: () => wait({ liked: true, like_count: 12 }),
  unlike: () => wait({ liked: false, like_count: 11 }),
  prefetchDetail: () => {},

  track: () => Promise.resolve(),
};
